// Reveal animations from Webflow IX2:
//  - SlideIn: the site-wide "slide in from bottom" preset on every .padding-global section
//    (opacity 0 -> 1 and 100px -> 0 over 1s, outQuart, 500ms after it scrolls into view).
//  - HeroGlow: the home page "Hero Glow [Page Loaded]" fade-up.

import { useEffect, useRef, useState } from 'react';
import type { WfProps } from '../webflow/element';
import { css } from './easing';

// Lets the stylesheet know the interaction code is running (see interactions.css: content
// hidden for SlideIn is revealed by a CSS fallback if this script never loads).
if (typeof document !== 'undefined') document.documentElement.classList.add('wf-ix-ready');

export function SlideIn({ as: Tag = 'div', children, ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      el?.setAttribute('data-ix-shown', '');
      return undefined;
    }
    let anim: Animation | undefined;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      io.disconnect();
      anim = el.animate(
        [
          { opacity: 0, transform: 'translate3d(0, 100px, 0)' },
          { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        ],
        { duration: 1000, delay: 500, easing: css('outQuart'), fill: 'both' },
      );
      anim.onfinish = () => {
        // Hand the final state back to the stylesheet and drop the animation's fill.
        el.setAttribute('data-ix-shown', '');
        anim?.cancel();
      };
    });
    io.observe(el);
    return () => {
      io.disconnect();
      anim?.cancel();
      el.setAttribute('data-ix-shown', '');
    };
  }, []);
  return (
    <Tag ref={ref} data-ix="slide-in" {...props}>
      {children}
    </Tag>
  );
}

export function HeroGlow({ as: Tag = 'div', style, children, ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el?.animate) {
      setDone(true);
      return undefined;
    }
    const move = el.animate([{ transform: 'translate3d(0, 5rem, 0)' }, { transform: 'translate3d(0, 0, 0)' }], {
      duration: 1000,
      delay: 500,
      easing: css('outQuad'),
      fill: 'both',
    });
    const fade = el.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 1500,
      delay: 500,
      easing: css('outQuad'),
      fill: 'both',
    });
    let cancelled = false;
    void Promise.all([move.finished, fade.finished])
      .then(() => !cancelled && setDone(true))
      .catch(() => undefined);
    return () => {
      cancelled = true;
      move.cancel();
      fade.cancel();
    };
  }, []);
  // Server render keeps Webflow's initial state (shifted down, transparent).
  const finalStyle = {
    ...style,
    WebkitTransform: undefined,
    MozTransform: undefined,
    msTransform: undefined,
    transform: 'translate3d(0px, 0px, 0px)',
    opacity: 1,
  };
  return (
    <Tag ref={ref} {...props} style={done ? finalStyle : style}>
      {children}
    </Tag>
  );
}
